import { useNavigate } from "react-router";
import { cn } from "@/lib/utils";
import { formatRelativeTime } from "@/lib/format";
import {
  buildNotificationContent,
  getNotificationIcon,
} from "@/lib/notification-utils";
import { useMarkAsRead } from "@/lib/tanstack-query/notifications";
import type { NotificationDto } from "@/lib/tanstack-query/dtos/notification.dto";

interface NotificationItemProps {
  notification: NotificationDto;
  onClose: () => void;
}

export function NotificationItem({
  notification,
  onClose,
}: NotificationItemProps) {
  const navigate = useNavigate();
  const markAsRead = useMarkAsRead();
  const Icon = getNotificationIcon(notification.type);
  const { message, url } = buildNotificationContent(
    notification.type,
    notification.payload,
  );

  const handleClick = () => {
    if (!notification.isRead) {
      markAsRead.mutate(notification.id);
    }
    onClose();
    navigate(url);
  };

  return (
    <button
      onClick={handleClick}
      role="link"
      className={cn(
        "flex items-start gap-3 w-full px-4 py-3 text-left transition-colors hover:bg-muted/50 cursor-pointer",
        !notification.isRead && "bg-muted/30",
      )}
    >
      <div className="shrink-0 mt-0.5 p-1.5 rounded-full bg-muted">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm leading-snug line-clamp-2">{message}</p>
        <p className="text-xs text-muted-foreground mt-1">
          {formatRelativeTime(new Date(notification.createdAt))}
        </p>
      </div>
      {!notification.isRead && (
        <div className="shrink-0 mt-2">
          <span
            className="block h-2 w-2 rounded-full bg-primary"
            aria-label="Unread"
          />
        </div>
      )}
    </button>
  );
}
